import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../lib/LanguageContext";
import { CONTACT } from "../lib/translations";
import PageHeader from "../components/PageHeader";
import EnquiryForm from "../components/EnquiryForm";
import { MapPin, Phone, Mail, Clock, MessageCircle, Navigation } from "lucide-react";

const Contact = () => {
  const { t, lang } = useLang();
  const p = t.contactPg;
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#enquiry") {
      setTimeout(() => {
        const el = document.getElementById("enquiry");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [location.hash]);

  const wa = `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(
    lang === "mr" ? "नमस्कार! मला शाळेबद्दल माहिती हवी आहे." : "Hello! I'd like to know about the school."
  )}`;
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CONTACT.mapQuery)}`;
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&output=embed`;

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow={p.eyebrow} title={p.title} subtitle={p.sub} testId="contact-header" />

      {/* Contact cards */}
      <section className="section-pad">
        <div className="container-school grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="card-soft p-6" data-testid="contact-address-card">
            <div className="w-12 h-12 rounded-xl bg-school-primary/10 flex items-center justify-center mb-4">
              <MapPin className="w-6 h-6 text-school-primary" />
            </div>
            <h3 className="font-heading font-bold text-lg text-school-text">{p.address}</h3>
            <p className="mt-2 text-sm text-school-muted leading-relaxed">{lang === "mr" ? CONTACT.addressMr : CONTACT.addressEn}</p>
          </div>
          <div className="card-soft p-6" data-testid="contact-phone-card">
            <div className="w-12 h-12 rounded-xl bg-school-accent/10 flex items-center justify-center mb-4">
              <Phone className="w-6 h-6 text-school-accent" />
            </div>
            <h3 className="font-heading font-bold text-lg text-school-text">{p.phone}</h3>
            <div className="mt-2 flex flex-col gap-1">
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="contact-phone-primary" className="text-base text-school-muted hover:text-school-primary transition-colors font-heading font-semibold">
                {lang === "mr" ? CONTACT.phonePrimary : CONTACT.phonePrimaryEn}
              </a>
              <a href={`tel:${CONTACT.phoneRawSecondary}`} data-testid="contact-phone-secondary" className="text-base text-school-muted hover:text-school-primary transition-colors font-heading font-semibold">
                {lang === "mr" ? CONTACT.phoneSecondary : CONTACT.phoneSecondaryEn}
              </a>
            </div>
          </div>
          <div className="card-soft p-6" data-testid="contact-email-card">
            <div className="w-12 h-12 rounded-xl bg-school-secondary/20 flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-school-primary" />
            </div>
            <h3 className="font-heading font-bold text-lg text-school-text">{p.email}</h3>
            <a href={`mailto:${CONTACT.email}`} className="mt-2 text-sm text-school-muted hover:text-school-primary transition-colors break-all block">{CONTACT.email}</a>
          </div>
          <div className="card-soft p-6" data-testid="contact-hours-card">
            <div className="w-12 h-12 rounded-xl bg-school-primary/10 flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-school-primary" />
            </div>
            <h3 className="font-heading font-bold text-lg text-school-text">{p.hours}</h3>
            <p className="mt-2 text-sm text-school-muted leading-relaxed">{p.hoursVal}</p>
          </div>
        </div>

        {/* CTAs */}
        <div className="container-school mt-8 flex flex-wrap gap-3 justify-center">
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-school-accent text-white font-heading font-semibold hover:bg-[var(--school-accent-dark)] transition-colors">
            <MessageCircle className="w-5 h-5" /> {p.whatsappNow}
          </a>
          <a href={directions} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-btn" className="btn-outline">
            <Navigation className="w-5 h-5" /> {p.getDirections}
          </a>
        </div>
      </section>

      {/* Map */}
      <section className="pb-8">
        <div className="container-school">
          <div className="rounded-3xl overflow-hidden shadow-xl border border-school" data-testid="contact-map">
            <iframe
              title="School Location"
              src={mapEmbed}
              width="100%"
              height="420"
              loading="lazy"
              style={{ border: 0 }}
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquiry" className="section-pad bg-school-primary relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-school-secondary/10 rounded-full blur-3xl pointer-events-none" />
        <div className="container-school relative grid lg:grid-cols-2 gap-12 items-start">
          <div className="text-white">
            <span className="eyebrow !text-school-secondary">{t.enquiry.eyebrow}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl">{t.enquiry.title}</h2>
            <p className="mt-5 text-base md:text-lg text-white/90">{t.enquiry.sub}</p>
          </div>
          <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl">
            <EnquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
